<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import web1 from '@/assets/img/home/page5/page5-web1.png';
import web2 from '@/assets/img/home/page5/page5-web2.png';
import web3 from '@/assets/img/home/page5/page5-web3.png';
import web4 from '@/assets/img/home/page5/page5-web4.png';
import { useI18n } from 'vue-i18n'
const page5Ref = ref<HTMLElement | null>(null)
/* 国际化 */
const { t } = useI18n()
/* 标题进入动画（保持原逻辑） */
const titleRef = ref<HTMLElement | null>(null)
const isVisible = ref(false)

/* 4 个卡片 ref + sticky 偏移（px）*/
const cards = [
  { ref: ref<HTMLElement | null>(null), topPx: 48  }, // box01 3rem
  { ref: ref<HTMLElement | null>(null), topPx: 80  }, // box02 5rem
  { ref: ref<HTMLElement | null>(null), topPx: 112 }, // box03 7rem
  { ref: ref<HTMLElement | null>(null), topPx: 144 }  // box04 9rem
]

/* 计算 & 设置缩放 */
const handleScroll = () => {
  /* 标题显隐 */
  if (titleRef.value) {
    const { top, bottom } = titleRef.value.getBoundingClientRect()
    const winH = window.innerHeight
    isVisible.value = top <= winH * 0.8 && bottom >= 0
  }
  cards.forEach((c, i) => {
    const el = c.ref.value
    if (!el) return
    /* 默认不缩放 */
    let scale = 1
    /* --- 仅前两张参与插值 --- */
    if (i === 0) {
      /* 与 card1 的 1 → 0.95 */
      scale -= 0.05 * progressBetween(c, cards[1])
      /* 与 card2 的 0.95 → 0.90 */
      scale -= 0.05 * progressBetween(c, cards[2])
    } else if (i === 1) {
      /* 与 card2 的 1 → 0.95 */
      scale -= 0.05 * progressBetween(c, cards[2])
    }
    /* i >= 2 无需处理，保持 1 */
    el.style.transform = `scale(${scale})`
  })
}
/* 工具：计算 progress = 0~1 
   当 target.top 处于 [start(50%), end(sticky 顶)] 区间线性递增  */
const progressBetween = (prev:any, next:any) => {
    const prevEl = prev.ref.value
  const nextEl = next.ref.value
  if (!prevEl || !nextEl) return 0

  const h = prevEl.offsetHeight
  const start = prev.topPx + h * 0.5   // 50 %
  const end   = prev.topPx            // 顶到 sticky
  const topNext = nextEl.getBoundingClientRect().top

  return Math.max(0, Math.min(1, (start - topNext) / (start - end)))
}
onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()               // 初始校准
})
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <div id="page5-section" ref="page5Ref" class="bg-[#fff]  w-[78rem]  mx-auto py-[5rem]">
    <div 
      ref="titleRef" 
      class="flex flex-col transform transition-all duration-1000"
      :class="isVisible ? 'translate-y-0 opacity-100' : 'translate-y-[10rem] opacity-0'"
    >
    <div class="text-[3rem] font-[600]">
        {{ t('page5.title1') }}
      </div>
      <div class="text-[3rem] font-[600] mb-[1.8rem]">
        {{ t('page5.title2Prefix') }}
        <span class="text-main-primary">{{ t('page5.title2Highlight') }}</span>
      </div>
      <div class="text-[1.25rem] text-[#000] leading-[1.75rem]">
        {{ t('page5.descLine1') }}
      </div>
      <div class="text-[1.25rem] text-[#000] leading-[1.75rem]">
        {{ t('page5.descLine2') }}
      </div>
    </div>

    <!-- 01 私有化 -->
    <div :ref="cards[0].ref" class="sticky top-[3rem]  w-full mt-[4.125rem] p-[1.25rem] rounded-[1.25rem] flex justify-between h-[40rem] bg-[#E6ECFF] transform-gpu transition-transform ease-[cubic-bezier(.25,.8,.25,1)] duration-300">
      <div class="flex flex-col justify-between mt-[8.375rem] ml-[1.3rem] text-[#0040C1]">
        <div>
          <div class="leading-[2.3rem] font-[600] text-[2.18rem]">
            <p>{{ t('page5.private.h1') }}</p><p>{{ t('page5.private.h2') }}</p>
          </div>
          <div class="w-[21.75rem] mt-[1.875rem] leading-[1.875rem] text-[1.25rem]">
            {{ t('page5.private.desc') }}
          </div>
        </div>
        <div class="text-[#396BD0] text-[1.25rem]">01</div>
      </div>
      <div class="w-[49.9rem] h-[37rem] mt-[0.3rem] rounded-[0.4rem] overflow-hidden">
        <img :src="web1" class="w-[49.9rem] h-[37rem] object-cover" />
      </div>
    </div>

    <!-- 02 销售 -->
    <div :ref="cards[1].ref" class="sticky top-[6rem] w-full mt-[4.125rem] p-[1.3rem] rounded-[1.25rem] flex justify-between h-[40rem] bg-[#D0E9FB] transform-gpu transition-transform ease-[cubic-bezier(.25,.8,.25,1)] duration-300">
      <div class="flex flex-col justify-between mt-[8.375rem] ml-[1.3rem] text-[#2F76A3]">
        <div>
          <div class="leading-[2.3rem] font-[600] text-[2.18rem]">
            <p>{{ t('page5.sales.h1') }}</p>
            <p>{{ t('page5.sales.h2') }}</p>
          </div>
          <div class="w-[21.75rem] mt-[1.875rem] leading-[1.875rem] text-[1.25rem]">
            {{ t('page5.sales.desc') }}
          </div>
        </div>
        <div class="text-[#4787B0] text-[1.25rem]">02</div>
      </div>
      <div class="web-shadow w-[49.9rem] h-[37rem] mt-[0.3rem] rounded-[0.4rem] overflow-hidden">
        <img :src="web2" class="w-[49.9rem] h-[37rem] object-cover" />
      </div>
    </div>

    <!-- 03 医疗 -->
    <div :ref="cards[2].ref" class="sticky top-[9.3rem] w-full mt-[4.125rem] p-[1.25rem] rounded-[1.25rem] flex justify-between h-[40rem] bg-[#E2F1DB] transform-gpu transition-transform ease-[cubic-bezier(.25,.8,.25,1)] duration-300">
      <div class="flex flex-col justify-between mt-[8.375rem] ml-[1.3rem] text-[#25570A]">
        <div>
          <div class="leading-[2.3rem] font-[600] text-[2.18rem]">
            <p>{{ t('page5.medical.h1') }}</p>
            <p>{{ t('page5.medical.h2') }}</p>
          </div>
          <div class="w-[21.75rem] mt-[1.875rem] leading-[1.875rem] text-[1.25rem]">
            {{ t('page5.medical.desc') }}
          </div>
        </div>
        <div class="text-[#25570A] text-[1.25rem]">03</div>
      </div>
      <div class="web-shadow w-[49.9rem] h-[37rem] mt-[0.3rem] rounded-[0.4rem] overflow-hidden">
        <img :src="web3" class="w-[49.9rem] h-[37rem] object-cover" />
      </div>
    </div>
    <!-- 04 教育 -->
    <div :ref="cards[3].ref" class="sticky top-[14rem] w-full mt-[4.125rem] p-[1.25rem] rounded-[1.25rem] flex justify-between h-[40rem] bg-[#EFE7DD] transform-gpu transition-transform ease-[cubic-bezier(.25,.8,.25,1)] duration-300">
      <div class="flex flex-col justify-between mt-[8.375rem] ml-[1.3rem] text-[#583C1C]">
        <div>
          <div class="leading-[2.3rem] font-[600] text-[2.18rem]">
            <p>{{ t('page5.education.h1') }}</p>
            <p >{{ t('page5.education.h2') }}</p>
          </div>
          <div class="w-[21.75rem] mt-[1.875rem] leading-[1.875rem] text-[1.25rem]">
            {{ t('page5.education.desc') }}
          </div>
        </div>
        <div class="text-[#583C1C] text-[1.25rem]">04</div>
      </div>
      <div class="web-shadow w-[49.9rem] h-[37rem] mt-[0.3rem] rounded-[0.4rem] overflow-hidden">
        <img :src="web4" class="w-[49.9rem] h-[37rem] object-cover" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.web-shadow {
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.3);
}

/* 优化动画性能 */
.transform {
  will-change: transform, opacity;
  backface-visibility: hidden;
}
</style>